import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { History, Trophy, Filter } from 'lucide-react';
import { usePublicRaffleDraws, usePublicRaffles } from '../hooks/useRaffles';
import { Container } from '../components/layout/Container';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Spinner } from '../components/common/Spinner';
import { Select } from '../components/common/Select';
import { Input } from '../components/common/Input';
import { formatCurrency, formatDateTime, formatPercentage } from '../utils/formatters';

export const DrawHistory: React.FC = () => {
  const { raffleId: paramRaffleId } = useParams<{ raffleId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [selectedRaffleId, setSelectedRaffleId] = useState(paramRaffleId || searchParams.get('raffleId') || '');
  const [drawDate, setDrawDate] = useState(searchParams.get('drawDate') || '');
  const [page, setPage] = useState(1);

  const { data: rafflesData } = usePublicRaffles({ limit: 100 });
  const { data, isLoading } = usePublicRaffleDraws(selectedRaffleId, {
    page, limit: 10, drawDate: drawDate || undefined,
  });

  useEffect(() => {
    const params: any = {};
    if (selectedRaffleId) params.raffleId = selectedRaffleId;
    if (drawDate) params.drawDate = drawDate;
    setSearchParams(params);
  }, [selectedRaffleId, drawDate, setSearchParams]);

  const raffles = rafflesData?.data?.raffles || [];
  const drawsData = data?.data;
  const draws = drawsData?.draws || [];
  const pagination = drawsData?.pagination;
  const raffle = drawsData?.raffle;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
      <Container className="py-10">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <History className="h-6 w-6 text-indigo-600" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Draw History</h1>
          </div>
          <p className="text-gray-500 dark:text-gray-400">View winning ticket numbers from past draws</p>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-4 w-4 text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Filters</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Select Raffle"
              value={selectedRaffleId}
              onChange={(e) => { setSelectedRaffleId(e.target.value); setPage(1); }}
              options={[
                { value: '', label: 'Choose a raffle...' },
                ...raffles.map((r) => ({ value: r.id, label: r.name })),
              ]}
            />
            <Input
              label="Draw Date (Optional)"
              type="date"
              value={drawDate}
              onChange={(e) => { setDrawDate(e.target.value); setPage(1); }}
            />
            <div className="flex items-start md:mt-6">
              <Button variant="outline" onClick={() => { setDrawDate(''); setPage(1); }} fullWidth>
                Clear Date Filter
              </Button>
            </div>
          </div>
        </Card>

        {!selectedRaffleId ? (
          <Card>
            <div className="text-center py-16">
              <History className="h-12 w-12 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">Select a raffle to view its draw history</p>
            </div>
          </Card>
        ) : isLoading ? (
          <Card>
            <div className="flex justify-center py-16"><Spinner size="lg" /></div>
          </Card>
        ) : draws.length === 0 ? (
          <Card>
            <div className="text-center py-16">
              <Trophy className="h-12 w-12 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">
                {drawDate ? 'No draws found for the selected date' : 'No draws have been completed for this raffle yet'}
              </p>
            </div>
          </Card>
        ) : (
          <>
            {raffle && (
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{raffle.name}</h2>
                <Link to={`/raffles/${raffle.id}`} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
                  View Raffle Details →
                </Link>
              </div>
            )}

            <div className="space-y-4">
              {draws.map((draw) => (
                <Card key={draw.id}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Draw on {formatDateTime(draw.drawDate)}
                      </h3>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">ID: {draw.id}</p>
                    </div>
                    <div className="sm:text-right">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Total Sales</p>
                      <p className="text-xl font-bold text-emerald-600">
                        {formatCurrency(draw.totalSales, raffle?.currency || 'USD')}
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-5">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Winners Share</p>
                    <p className="font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(
                        draw.winners.reduce((sum, w) => sum + w.prizeAmount, 0),
                        raffle?.currency || 'USD'
                      )}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">Winning Tickets</h4>
                    <div className="space-y-2">
                      {draw.winners.map((winner, index) => (
                        <div key={index} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-amber-50 dark:bg-amber-950 border border-amber-100 dark:border-amber-800 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-mono font-bold text-gray-900 dark:text-gray-100">{winner.ticketNumber}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {formatPercentage(winner.prizePercentage)} of pool
                              </p>
                            </div>
                          </div>
                          <p className="text-lg font-bold text-emerald-600 sm:text-right pl-11 sm:pl-0">
                            {formatCurrency(winner.prizeAmount, raffle?.currency || 'USD')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {pagination && pagination.pages > 1 && (
              <div className="flex justify-center items-center gap-4 mt-8">
                <Button variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</Button>
                <span className="text-sm text-gray-500 dark:text-gray-400">Page {page} of {pagination.pages}</span>
                <Button variant="outline" disabled={page === pagination.pages} onClick={() => setPage(page + 1)}>Next</Button>
              </div>
            )}
          </>
        )}
      </Container>
    </div>
  );
};
